import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';

const jobTypes    = ['Full Time', 'Part Time'];
const categories  = ['Technology', 'Marketing', 'Design', 'HR', 'Finance', 'Education', 'Healthcare', 'Sales'];
const experiences = ['Freshers', 'Experience', '5+ experience'];

export default function JobList() {
  const [jobs, setJobs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [keyword, setKeyword]   = useState('');
  const [location, setLocation] = useState('');
  const [types, setTypes]       = useState([]);
  const [cats, setCats]         = useState([]);
  const [exps, setExps]         = useState([]);
  const [sortBy, setSortBy]     = useState('newest');
  const [viewMode, setViewMode] = useState('list');
  const [saved, setSaved]       = useState([]);
  const [total, setTotal]       = useState(0);
  const { user } = useAuth();

  const fetchJobs = async (kw = keyword, loc = location, t = types, c = cats, e = exps) => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (kw)  params.append('keyword', kw);
      if (loc) params.append('location', loc);
      t.forEach(v => params.append('type', v));
      c.forEach(v => params.append('category', v));
      e.forEach(v => params.append('experience', v));
      params.append('limit', 20);

      const [localRes, externalRes] = await Promise.allSettled([
        fetch(`http://localhost:5001/api/jobs?${params}`).then(r => r.json()),
        fetch(`http://localhost:5001/api/jobs/external?keyword=${kw || 'jobs'}&location=${loc}`).then(r => r.json()),
      ]);

      const localJobs    = localRes.status    === 'fulfilled' ? (localRes.value.jobs    || []) : [];
      const externalJobs = externalRes.status === 'fulfilled' ? (externalRes.value.jobs || []) : [];
      const merged = [...localJobs, ...externalJobs];
      setJobs(merged);
      setTotal(merged.length);
    } catch {
      setError('Failed to load jobs. Make sure the backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); }, [keyword, location, types, cats, exps]);

  const toggle = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val]);

  const toggleSave = (i) =>
    setSaved(saved.includes(i) ? saved.filter(s => s !== i) : [...saved, i]);

  const handleSearch = (e) => { e.preventDefault(); fetchJobs(); };

  const clearFilters = () => { setTypes([]); setCats([]); setExps([]); };

  return (
    <>
      <PageHeader title="Job Listings" breadcrumb="Job List" />

      {/* Search Bar */}
      <div className="jl-search-bar">
        <div className="container">
          <form className="jl-search-inner" onSubmit={handleSearch}>
            <div className="jl-search-field">
              <i className="fa fa-search"></i>
              <input type="text" placeholder="Job title or keyword..."
                value={keyword} onChange={e => setKeyword(e.target.value)} />
            </div>
            <div className="jl-search-sep"></div>
            <div className="jl-search-field">
              <i className="fa fa-map-marker-alt"></i>
              <input type="text" placeholder="City, country or remote..."
                value={location} onChange={e => setLocation(e.target.value)} />
            </div>
            <button type="submit" className="jl-search-btn">
              <i className="fa fa-search me-2"></i> Search Jobs
            </button>
          </form>
        </div>
      </div>

      <section className="section-pad">
        <div className="container">
          <div className="jl-layout">

            {/* Sidebar */}
            <aside className="jl-sidebar">
              {(types.length > 0 || cats.length > 0 || exps.length > 0) && (
                <div className="jl-filter-card">
                  <div className="jl-filter-title-row">
                    <h6>Active Filters</h6>
                    <button onClick={clearFilters} className="jl-clear-btn">Clear All</button>
                  </div>
                  <div className="jl-active-tags">
                    {[...types, ...cats, ...exps].map((t, i) => (
                      <span key={i} className="jl-active-tag">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="jl-filter-card">
                <h6 className="jl-filter-title">Job Type</h6>
                {jobTypes.map((t, i) => (
                  <label key={i} className="jl-checkbox-label">
                    <input type="checkbox" checked={types.includes(t)} onChange={() => toggle(types, setTypes, t)} />
                    <span className="jl-checkbox-custom"></span>
                    <span>{t}</span>
                  </label>
                ))}
              </div>

              <div className="jl-filter-card">
                <h6 className="jl-filter-title">Category</h6>
                {categories.map((c, i) => (
                  <label key={i} className="jl-checkbox-label">
                    <input type="checkbox" checked={cats.includes(c)} onChange={() => toggle(cats, setCats, c)} />
                    <span className="jl-checkbox-custom"></span>
                    <span>{c}</span>
                  </label>
                ))}
              </div>

              <div className="jl-filter-card">
                <h6 className="jl-filter-title">Experience Level</h6>
                {experiences.map((e, i) => (
                  <label key={i} className="jl-checkbox-label">
                    <input type="checkbox" checked={exps.includes(e)} onChange={() => toggle(exps, setExps, e)} />
                    <span className="jl-checkbox-custom"></span>
                    <span>{e}</span>
                  </label>
                ))}
              </div>
              {!user && (
                <div className="jl-sidebar-cta">
                  <i className="fa fa-briefcase"></i>
                  <h6>Post a Job</h6>
                  <p>Reach thousands of qualified candidates today.</p>
                  <Link to="/post-job" className="btn-grad w-100 justify-content-center">Get Started</Link>
                </div>
              )}
            </aside>

            {/* Results */}
            <div className="jl-results">
              <div className="jl-toolbar">
                <p className="jl-result-count">
                  {loading ? 'Loading...' : <>Showing <strong>{jobs.length}</strong> of <strong>{total}</strong> jobs</>}
                </p>
                <div className="jl-toolbar-right">
                  <select className="jl-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                    <option value="newest">Newest First</option>
                    <option value="salary">Highest Salary</option>
                  </select>
                  <div className="jl-view-toggle">
                    <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>
                      <i className="fa fa-list"></i>
                    </button>
                    <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>
                      <i className="fa fa-th-large"></i>
                    </button>
                  </div>
                </div>
              </div>

              {loading && (
                <div className="text-center py-5">
                  <i className="fa fa-spinner fa-spin fa-2x text-primary"></i>
                  <p className="text-muted mt-3">Loading jobs...</p>
                </div>
              )}

              {error && <div className="auth-alert error"><i className="fa fa-exclamation-circle me-2"></i>{error}</div>}

              {!loading && !error && jobs.length === 0 && (
                <div className="jl-empty">
                  <i className="fa fa-search"></i>
                  <h5>No jobs found</h5>
                  <p>Try adjusting your filters or search terms</p>
                </div>
              )}

              {!loading && jobs.length > 0 && (
                <div className={viewMode === 'grid' ? 'jl-grid-view' : 'jl-list-view'}>
                  {jobs.map((job, i) => (
                    viewMode === 'list' ? (
                      <div className="jl-list-card" key={job._id || i}>
                        <div className="jl-list-card-left">
                          <div className="jl-logo">
                            {job.logo
                              ? <img src={job.logo} alt="" />
                              : <div style={{ width: '100%', height: '100%', background: '#e8f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0066FF', fontSize: 22 }}><i className="fa fa-building"></i></div>
                            }
                          </div>
                          <div className="jl-info">
                            <div className="jl-info-top">
                              <h5>{job.title}</h5>
                              <span className={`jl-tag tag-${job.isExternal ? 'external' : job.tag?.toLowerCase()}`}>
                                {job.isExternal ? '🌐 External' : job.tag}
                              </span>
                            </div>
                            <p className="jl-company">{job.company}</p>
                            <div className="jl-meta">
                              <span><i className="fa fa-map-marker-alt"></i>{job.location}</span>
                              <span><i className="far fa-clock"></i>{job.type}</span>
                              <span><i className="far fa-money-bill-alt"></i>{(job.salary || 'Negotiable').replace(/\$/g, '₹')}</span>
                              <span><i className="far fa-calendar-alt"></i>{new Date(job.deadline).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="jl-list-card-right">
                          <button className={`jl-save-btn ${saved.includes(i) ? 'saved' : ''}`} onClick={() => toggleSave(i)}>
                            <i className={`${saved.includes(i) ? 'fa' : 'far'} fa-heart`}></i>
                          </button>
                          {job.isExternal
                            ? <a href={job.applyUrl} target="_blank" rel="noreferrer" className="jl-apply-btn">Apply Now</a>
                            : <Link to={`/job-detail/${job._id}`} className="jl-apply-btn">Apply Now</Link>
                          }
                        </div>
                      </div>
                    ) : (
                      <div className="jl-grid-card" key={job._id || i}>
                        <div className="jl-grid-card-top">
                          <div className="jl-logo" style={{ width: 50, height: 50 }}>
                            {job.logo
                              ? <img src={job.logo} alt="" />
                              : <div style={{ width: '100%', height: '100%', background: '#e8f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0066FF' }}><i className="fa fa-building"></i></div>
                            }
                          </div>
                          <span className={`jl-tag tag-${job.tag?.toLowerCase()}`}>{job.tag}</span>
                        </div>
                        <h5>{job.title}</h5>
                        <p className="jl-company">{job.company}</p>
                        <div className="jl-grid-meta">
                          <span><i className="fa fa-map-marker-alt"></i>{job.location}</span>
                          <span><i className="far fa-clock"></i>{job.type}</span>
                          <span><i className="far fa-money-bill-alt"></i>{(job.salary || 'Negotiable').replace(/\$/g, '₹')}</span>
                        </div>
                        <div className="jl-grid-footer">
                          <button className={`jl-save-btn ${saved.includes(i) ? 'saved' : ''}`} onClick={() => toggleSave(i)}>
                            <i className={`${saved.includes(i) ? 'fa' : 'far'} fa-heart`}></i>
                          </button>
                          {job.isExternal
                            ? <a href={job.applyUrl} target="_blank" rel="noreferrer" className="jl-apply-btn">Apply Now</a>
                            : <Link to={`/job-detail/${job._id}`} className="jl-apply-btn">Apply Now</Link>
                          }
                        </div>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
